import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    Chip,
    Avatar,
    Paper,
    Stack,
    LinearProgress,
    Button
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    TrendingUp as TrendingUpIcon,
    Insights as InsightsIcon,
    AutoAwesome as AutoAwesomeIcon,
    CheckCircle as CheckCircleIcon,
    BugReport as BugIcon,
    CompareArrows as CompareIcon,
    Apple as AppleIcon,
    Shop as GooglePlayIcon,
    Psychology as PsychologyIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

const SocialSentiment = () => {
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [activeTab, setActiveTab] = useState(0);

    // Mock Live Reviews for the Hero Animation
    const [liveReviews, setLiveReviews] = useState([
        { id: 1, source: 'App Store', user: 'Ahmet K.', rating: 1, comment: 'Son güncellemeden sonra uygulama sürekli çöküyor!', sentiment: 'negative', tag: 'Crash' },
        { id: 2, source: 'Play Store', user: 'Zeynep B.', rating: 5, comment: 'Arayüz çok hızlanmış, harika!', sentiment: 'positive', tag: 'UX/UI' },
        { id: 3, source: 'App Store', user: 'Mehmet Y.', rating: 2, comment: 'Ödeme ekranında hata alıyorum.', sentiment: 'negative', tag: 'Bug' },
    ]);

    useEffect(() => {
        const interval = setInterval(() => {
            setLiveReviews(prev => {
                const newReview = {
                    id: Date.now(),
                    source: Math.random() > 0.5 ? 'App Store' : 'Play Store',
                    user: `Kullanıcı ${Math.floor(Math.random() * 1000)}`,
                    rating: Math.floor(Math.random() * 5) + 1,
                    comment: Math.random() > 0.5 ? 'Harika bir deneyim!' : 'Giriş yapamıyorum, hata veriyor.',
                    sentiment: Math.random() > 0.5 ? 'positive' : 'negative',
                    tag: Math.random() > 0.5 ? 'Performance' : 'Login'
                };
                return [newReview, ...prev.slice(0, 2)];
            });
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const features = [
        {
            icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
            title: 'Otomatik Yorum Toplama',
            description: 'Sosyal medya ve uygulama mağazalarındaki tüm kullanıcı yorumlarını otomatik olarak tek merkezde toplar.',
            color: '#7c4dff'
        },
        {
            icon: <PsychologyIcon sx={{ fontSize: 40 }} />,
            title: 'Yapay Zeka Sınıflandırma',
            description: 'Yorumları gelişmiş NLP ile pozitif, negatif ve nötr olarak anlık olarak sınıflandırır.',
            color: '#ff5252'
        },
        {
            icon: <CompareIcon sx={{ fontSize: 40 }} />,
            title: 'Marka Algısı ve Rakip Analizi',
            description: 'Hizmet kalitesi, fiyat ve güvenilirlik temalarında marka algısını ölçer ve rakiplerle kıyaslar.',
            color: '#448aff'
        },
        {
            icon: <InsightsIcon sx={{ fontSize: 40 }} />,
            title: 'Trend ve Kelime Analizi',
            description: 'En sık geçen kelimeleri, temaları ve hashtag’leri analiz ederek görsel trend raporları üretir.',
            color: '#00bfa5'
        },
        {
            icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
            title: 'Zaman Serisi Analizleri',
            description: 'Trend takibi ve zaman serisi analizleri yaparak marka algısındaki değişimleri raporlar.',
            color: '#ffab40'
        }
    ];

    const dataSources = [
        {
            icon: <AppleIcon sx={{ fontSize: 40 }} />,
            title: 'App Store Connect',
            desc: 'iOS yorumları, sürüm verileri ve bölge bazlı analiz.',
            color: '#000000',
            bg: '#f5f5f7'
        },
        {
            icon: <GooglePlayIcon sx={{ fontSize: 40 }} />,
            title: 'Google Play Console',
            desc: 'Android vitals, cihaz modelleri ve yıldız puanları.',
            color: '#34a853',
            bg: '#e8f5e9'
        }
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            {/* Premium Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #101026 0%, #1e1e40 100%)',
                    color: 'white',
                    pt: { xs: 12, md: 16 },
                    pb: { xs: 12, md: 16 },
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Background Decor Elements */}
                <Box
                    component={motion.div}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 150, repeat: Infinity, ease: 'linear' }}
                    sx={{
                        position: 'absolute',
                        top: '-20%',
                        right: '-10%',
                        width: '800px',
                        height: '800px',
                        background: 'radial-gradient(circle, rgba(124, 77, 255, 0.15) 0%, transparent 70%)',
                        borderRadius: '50%',
                        zIndex: 0
                    }}
                />
                <Box
                    component={motion.div}
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                    sx={{
                        position: 'absolute',
                        bottom: '-10%',
                        left: '-10%',
                        width: '600px',
                        height: '600px',
                        background: 'radial-gradient(circle, rgba(68, 138, 255, 0.1) 0%, transparent 70%)',
                        borderRadius: '50%',
                        zIndex: 0
                    }}
                />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={8} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <Chip
                                    label="D-CAT Mobil Analitik"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        color: '#b388ff',
                                        border: '1px solid rgba(179, 136, 255, 0.3)',
                                        mb: 3,
                                        fontWeight: 600,
                                        px: 1
                                    }}
                                />
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: '2.5rem', md: '4rem' },
                                        mb: 2,
                                        background: 'linear-gradient(90deg, #fff 0%, #b388ff 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        lineHeight: 1.1
                                    }}
                                >
                                    Marka Algınızı Veriye Dönüştürün
                                </Typography>
                                <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, lineHeight: 1.6, fontWeight: 300, maxWidth: 600 }}>
                                    Markanızın sosyal medya, uygulama mağazaları ve tüm dijital platformlardaki performansını anlık izleyen; kullanıcı yorumlarını yapay zeka ile analiz ederek marka algınızı stratejik raporlara dönüştüren sosyal medya duygu ve marka algı analizi asistanı.
                                </Typography>

                                <Stack direction="row" spacing={2}>
                                    <Chip
                                        icon={<CheckCircleIcon sx={{ color: '#00e676 !important' }} />}
                                        label="Canlı Veri Akışı"
                                        sx={{ bgcolor: 'rgba(0, 230, 118, 0.1)', color: '#00e676', border: '1px solid rgba(0, 230, 118, 0.2)' }}
                                    />
                                    <Chip
                                        icon={<AutoAwesomeIcon sx={{ color: '#b388ff !important' }} />}
                                        label="%98 Başarı Oranı"
                                        sx={{ bgcolor: 'rgba(179, 136, 255, 0.1)', color: '#b388ff', border: '1px solid rgba(179, 136, 255, 0.2)' }}
                                    />
                                </Stack>
                            </motion.div>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <Paper
                                    elevation={24}
                                    sx={{
                                        background: 'rgba(26, 26, 46, 0.95)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        boxShadow: '0 20px 80px rgba(0,0,0,0.5)'
                                    }}
                                >
                                    {/* Dashboard Header */}
                                    <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar sx={{ bgcolor: '#7c4dff', width: 32, height: 32 }}>
                                                <InsightsIcon sx={{ fontSize: 18 }} />
                                            </Avatar>
                                            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                                                Canlı Analiz Paneli
                                            </Typography>
                                        </Stack>
                                        <Chip
                                            label="LIVE"
                                            size="small"
                                            sx={{
                                                bgcolor: '#ff1744',
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: '0.7rem',
                                                height: 20
                                            }}
                                        />
                                    </Box>

                                    {/* Dashboard Content */}
                                    <Box sx={{ p: 3 }}>
                                        {/* Stats Row */}
                                        <Grid container spacing={2} sx={{ mb: 3 }}>
                                            <Grid item xs={4}>
                                                <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Duygu Skoru</Typography>
                                                    <Typography variant="h6" sx={{ color: '#00e676', fontWeight: 700 }}>4.8/5.0</Typography>
                                                    <LinearProgress variant="determinate" value={96} sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#00e676' } }} />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Son 24s</Typography>
                                                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>1,204</Typography>
                                                    <Typography variant="caption" sx={{ color: '#00e676' }}>+12% Yorum</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Kritik Hata</Typography>
                                                    <Typography variant="h6" sx={{ color: '#ff1744', fontWeight: 700 }}>3</Typography>
                                                    <Typography variant="caption" sx={{ color: '#ff1744' }}>Acil Bakılmalı</Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        {/* Live Feed */}
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
                                            SON GELEN YORUMLAR (ANLIK)
                                        </Typography>

                                        <Box sx={{ maxHeight: 200, overflow: 'hidden' }}>
                                            <AnimatePresence>
                                                {liveReviews.map((review) => (
                                                    <motion.div
                                                        key={review.id}
                                                        initial={{ opacity: 0, y: -20, height: 0 }}
                                                        animate={{ opacity: 1, y: 0, height: 'auto' }}
                                                        exit={{ opacity: 0, y: 20 }}
                                                        transition={{ duration: 0.5 }}
                                                    >
                                                        <Paper
                                                            sx={{
                                                                p: 1.5,
                                                                mb: 1.5,
                                                                bgcolor: 'rgba(255,255,255,0.03)',
                                                                borderLeft: `3px solid ${review.sentiment === 'positive' ? '#00e676' : '#ff1744'}`,
                                                                borderRadius: 1
                                                            }}
                                                        >
                                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>{review.user}</Typography>
                                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>• {review.source}</Typography>
                                                                </Stack>
                                                                <Chip label={review.tag} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }} />
                                                            </Stack>
                                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                                                                "{review.comment}"
                                                            </Typography>
                                                        </Paper>
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </Box>
                                    </Box>
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Container maxWidth="xl" sx={{ py: 12 }}>
                <Box textAlign="center" mb={8}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 2 }}>
                        Veriyi Anlamlı İçgörüye Dönüştürün
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 800, mx: 'auto' }}>
                        Milyonlarca satır veriyi manuel analiz etmeyin. Yapay zekamız sizin için kalıpları, hataları ve fırsatları bulsun.
                    </Typography>
                </Box>

                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} md={6} lg={3} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                viewport={{ once: true }}
                            >
                                <Card
                                    sx={{
                                        height: '100%',
                                        p: 4,
                                        borderRadius: 4,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                        border: '1px solid rgba(0,0,0,0.05)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-10px)',
                                            boxShadow: `0 15px 30px ${feature.color}20`,
                                            borderColor: feature.color
                                        }
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: `${feature.color}15`,
                                            color: feature.color,
                                            width: 64,
                                            height: 64,
                                            mb: 3
                                        }}
                                    >
                                        {feature.icon}
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e' }}>
                                        {feature.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                        {feature.description}
                                    </Typography>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Integrations */}
            <Box sx={{ bgcolor: 'white', py: 12 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 3 }}>
                                Tüm Platformlar <br />Tek Merkezde
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
                                iOS ve Android uygulamalarınızın tüm metriklerini tek bir dashboard üzerinden takip edin.
                                Sürüm karşılaştırması yapın, bölgesel kırılımları inceleyin.
                            </Typography>
                            <Stack direction="row" spacing={2}>
                                {dataSources.map((source, idx) => (
                                    <Paper
                                        key={idx}
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            bgcolor: source.bg,
                                            borderRadius: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            border: '1px solid rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        <Avatar sx={{ bgcolor: 'transparent', color: source.color }}>{source.icon}</Avatar>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold">{source.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">Tam Entegrasyon</Typography>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                viewport={{ once: true }}
                            >
                                <Paper
                                    elevation={10}
                                    sx={{
                                        p: 1,
                                        borderRadius: 4,
                                        bgcolor: '#f5f5f7',
                                        transform: 'rotate(-2deg)'
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop"
                                        sx={{
                                            width: '100%',
                                            borderRadius: 3,
                                            display: 'block'
                                        }}
                                    />
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Ücretsiz Demo Talep Et CTA */}
            <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                sx={{
                    py: { xs: 8, md: 12 },
                    background: 'linear-gradient(180deg, #0b1426 0%, #0a1020 100%)',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            color: '#ffffff',
                            mb: 3,
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                            textShadow: '2px 0 0 rgba(255,60,80,0.55), -2px 0 0 rgba(60,170,255,0.55)',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        Ücretsiz Demo Talep Et
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 720,
                            mx: 'auto',
                            mb: 5,
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            lineHeight: 1.7
                        }}
                    >
                        Yapay zeka çözümlerimizin işletmenize katacağı değeri birlikte keşfedelim. Hemen bizimle iletişime geçin ve uzman ekibimizden demo talep edin.
                    </Typography>
                    <Button
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => window.open('https://dcat.com.tr/classic/tr/iletisim', '_blank')}
                        sx={{
                            px: 5,
                            py: 1.75,
                            fontSize: '1.05rem',
                            fontWeight: 700,
                            color: '#fff',
                            borderRadius: 999,
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, #3b5cff 0%, #6a4cff 100%)',
                            boxShadow: '0 0 40px rgba(79, 92, 255, 0.55), 0 8px 24px rgba(0,0,0,0.35)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #4a6bff 0%, #7a5cff 100%)',
                                boxShadow: '0 0 55px rgba(106, 76, 255, 0.7), 0 10px 28px rgba(0,0,0,0.4)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Hemen Başlayın
                    </Button>
                </Container>
            </Box>
        </Box>
    );
};

export default SocialSentiment;
